import { FC } from 'react';
import { ReviewFinding, ReviewResultData, ReviewSeverity } from '@/types/assistant';

interface Props {
  result: ReviewResultData;
}

interface SeverityMeta {
  label: string;
  tone: string;
  chip: string;
}

const SEVERITY_META: Record<ReviewSeverity, SeverityMeta> = {
  erro: { label: 'Erro', tone: 'text-red-400', chip: 'bg-red-500/10 border-red-500/30' },
  aviso: { label: 'Aviso', tone: 'text-amber-400', chip: 'bg-amber-500/10 border-amber-500/30' },
  info: { label: 'Info', tone: 'text-content-tertiary', chip: 'bg-surface-tertiary border-surface-border' },
};

const CATEGORIA_LABEL: Record<string, string> = {
  formatacao: 'Formatação',
  numeracao: 'Numeração',
  siglas: 'Siglas',
  referencias: 'Referências',
  normas: 'Normas',
};
const CATEGORIA_ORDER = ['formatacao', 'numeracao', 'siglas', 'referencias', 'normas'];

function categoriaLabel(cat: string): string {
  return CATEGORIA_LABEL[cat] ?? cat;
}

/**
 * Agrupa os apontamentos por categoria, na mesma ordem fixa da devolutiva (Q05):
 * Formatação → Numeração → Siglas → Referências → Normas. Categoria desconhecida
 * (defensivo) cai no fim, na ordem em que apareceu.
 */
function groupByCategoria(findings: ReviewFinding[]): Array<[string, ReviewFinding[]]> {
  const map = new Map<string, ReviewFinding[]>();
  for (const f of findings) {
    const cat = f.categoria || 'outros';
    const list = map.get(cat) ?? [];
    list.push(f);
    map.set(cat, list);
  }
  const known = CATEGORIA_ORDER.filter((c) => map.has(c));
  const unknown = [...map.keys()].filter((c) => !CATEGORIA_ORDER.includes(c));
  return [...known, ...unknown].map((c) => [c, map.get(c) as ReviewFinding[]]);
}

const ReviewFindingRow: FC<{ finding: ReviewFinding }> = ({ finding }) => {
  const meta = SEVERITY_META[finding.severity] ?? SEVERITY_META.info;
  const hasLocation = finding.page != null || !!finding.item;
  return (
    <li
      data-testid="review-finding"
      data-severity={finding.severity}
      className="py-2.5 first:pt-0 last:pb-0 border-b border-surface-border last:border-0"
    >
      <div className="flex items-start gap-2">
        <span
          data-testid="review-severity"
          className={`shrink-0 text-2xs font-semibold px-1.5 py-0.5 rounded-md border ${meta.chip} ${meta.tone}`}
        >
          {meta.label}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-content-primary break-words">{finding.message}</span>
            <span
              data-testid="review-origem"
              className="shrink-0 text-2xs px-1.5 py-0.5 rounded bg-surface-tertiary border border-surface-border text-content-tertiary"
            >
              {finding.origem === 'ia' ? 'IA' : 'Automática'}
            </span>
          </div>

          {hasLocation && (
            <p className="mt-0.5 text-2xs text-content-tertiary">
              {finding.page != null && <span>página {finding.page}</span>}
              {finding.page != null && finding.item && ' · '}
              {finding.item && <span>item {finding.item}</span>}
            </p>
          )}

          {finding.snippet && (
            <p
              data-testid="review-snippet"
              title={finding.snippet}
              className="mt-1 truncate font-mono text-2xs text-content-tertiary bg-surface-tertiary rounded px-1.5 py-1"
            >
              {finding.snippet}
            </p>
          )}
        </div>
      </div>
    </li>
  );
};

/**
 * Resultado estruturado da revisão do H.Rev (Frente 7, Q03/Q04 → Q06). Renderizado
 * abaixo da resposta a partir de `Message.metadata.review`: resumo por categoria,
 * apontamentos agrupados (severidade/página-item/trecho/origem) e o disclaimer.
 * Mesmo dado, mesma UI ao vivo (evento `done`) ou no histórico (metadata persistida).
 */
const ReviewResult: FC<Props> = ({ result }) => {
  const findings = result.findings ?? [];
  if (findings.length === 0) return null;

  const grouped = groupByCategoria(findings);
  const total = result.resumo?.total ?? findings.length;
  const breakdown = grouped
    .map(([cat, items]) => `${items.length} ${categoriaLabel(cat).toLowerCase()}`)
    .join(' · ');

  return (
    <div data-testid="review-result" className="mt-3 pt-3 border-t border-surface-border">
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <span className="material-symbols-outlined text-accent text-lg leading-none">fact_check</span>
        <span data-testid="review-summary" className="text-sm font-semibold text-content-primary">
          {total} apontamento{total === 1 ? '' : 's'}
        </span>
        {breakdown && <span className="text-xs text-content-tertiary">· {breakdown}</span>}
        {result.source_file && (
          <span className="text-2xs text-content-tertiary truncate">· {result.source_file}</span>
        )}
      </div>

      <div className="rounded-lg bg-surface-secondary border border-surface-border px-3 py-1 divide-y divide-surface-border">
        {grouped.map(([cat, items]) => (
          <div key={cat} data-testid="review-category" data-categoria={cat} className="py-2 first:pt-1 last:pb-1">
            <p className="text-2xs uppercase tracking-wide text-content-tertiary mb-1">{categoriaLabel(cat)}</p>
            <ul>
              {items.map((f, i) => (
                <ReviewFindingRow key={`${f.rule_id}:${i}`} finding={f} />
              ))}
            </ul>
          </div>
        ))}
      </div>

      {result.disclaimer && (
        <p className="mt-2 text-2xs text-content-tertiary flex items-start gap-1">
          <span className="material-symbols-outlined text-amber-400 text-sm leading-none shrink-0">info</span>
          <span>{result.disclaimer}</span>
        </p>
      )}
    </div>
  );
};

export default ReviewResult;
